// Cashier discount limits and manager approval of over-limit discounts.
//
// The backend is the single source of truth (ops_discount_limit runs on
// /cart/preview, /cart/commit and the item-update route). This module only
// lets the POS pre-check and label, and carries a manager's PIN to the
// backend. Pure: no I/O, no zustand, no React -- the node:test file next to it
// runs it directly.
//
// ## What an approval is bound to (the binding contract, both repos)
//
//   line    product | lot | qty (exact) | per-unit discount (cents) | manual price
//   price   product | lot | qty (exact) | unit price (cents)
//   receipt kind (percent/amount) | value | base (eligible subtotal, cents)
//
// `ApprovalTuple` is that tuple and `signatureOf` its string form. The
// receipt base is the eligible subtotal the APPROVAL preview's server totals
// report (`eligibleBaseOf`), not a local figure; the approval also keeps the
// cart `basis` it was measured on (`receiptBasisKey`: customer, tax exemption
// and every line's product/lot/qty/price), so a cart edit that moves the base
// is seen locally before any preview answers. Any change to a bound field
// (qty +/-, a lot swap, a cart edit that moves the base, a value change)
// makes the approval stop matching: `attachApprovals` no longer sends its PIN
// and `pruneApprovals` drops it. Metadata (reason text, scan_confirmed) is
// not bound and never prunes. The line identity is product|lot: the preview
// bridge keys cart lines by it, so it is the most specific identity the cart
// has. Gift-card lines (manual_price) never get a tuple and never carry a PIN.
//
// ## How the approving PIN travels (the "pin transport")
//
// The backend gate is stateless across preview and commit: a PIN on
// /cart/preview is honoured for that request only, and the override row is
// written on /cart/commit. So an approval has to accompany every request that
// carries the approved value, or the next debounced preview (the cashier adds
// an item) and the commit would 409 `discount_exceeds_limit` again.
//
//   - `cartToRequest` attaches the PIN to exactly the request parts whose
//     current tuple matches an approval, on every preview and the commit;
//   - it lives only in memory (cartStore.discountApprovals is outside the
//     persist `partialize`, and the CFD snapshot never carries it), and is
//     dropped on customer change, clerk switch, refund mode, cart clear,
//     whenever posStore.currentReceiptId moves to a receipt other than the
//     one this cart just committed, on any override_* rejection (NOT on a
//     429 pin_throttled: the backend refuses EVERY PIN at a locked till,
//     a recently approved one included, for the rest of its 10-minute
//     window, so previews carrying the PIN fail until it lapses; the
//     approvals are kept so they work again afterwards, and the dialog
//     locks for 30 s, which is shorter than the server's window),
//     and -- only the refused tuple's -- on a 409
//     discount_exceeds_limit (`dropRefusedApprovals`). It is KEPT across
//     /cart/commit: a partially-paid receipt keeps previewing. Previews of a
//     bound receipt also send `receipt_id`, so the backend can reuse the
//     approvals already recorded on it (recall/unhold).
//   - it is recorded only when the approval preview's `discount_overrides`
//     shows a live row for the exact tuple (`confirmedApprovals`): a 2xx on
//     its own proves nothing.
// There is still no replayable "approved" flag: the server re-checks the PIN
// (or its own recorded row) on every request.

import type { Cart, CartDiscount, CartLine, DiscountOverrideRow, PreviewTotals } from './cart.ts'

/** 100 means "no limit" on the wire (`discount_limit_percent`). */
export const UNLIMITED_DISCOUNT_PERCENT = 100

/** Error codes of the discount-limit gate (v2 envelope `code`). */
export const DISCOUNT_EXCEEDS_LIMIT = 'discount_exceeds_limit'
/** The PIN was refused (wrong PIN, own PIN, or no approval rights: the
 *  backend deliberately doesn't say which), or the approver's own limit is
 *  below the discount. */
export const OVERRIDE_ERROR_CODES = [
  'override_refused',
  'override_limit_exceeded',
] as const
/** 429: too many PIN attempts from this register/staff. */
export const PIN_THROTTLED = 'pin_throttled'
/** How long the approval dialog refuses a submit after `pin_throttled`. */
export const PIN_THROTTLE_LOCK_MS = 30_000

/** Rejection carrying a lockout: ManagerApprovalDialog disables its submit
 *  for `lockoutMs` when onSubmit rejects with one. */
export class PinThrottledError extends Error {
  readonly lockoutMs: number
  constructor(message: string, lockoutMs = PIN_THROTTLE_LOCK_MS) {
    super(message)
    this.name = 'PinThrottledError'
    this.lockoutMs = lockoutMs
  }
}

export function isOverrideErrorCode(code: string | null | undefined): boolean {
  return !!code && (OVERRIDE_ERROR_CODES as readonly string[]).includes(code)
}

/** True for every code the discount-limit gate can answer with. */
export function isDiscountGateCode(code: string | null | undefined): boolean {
  return code === DISCOUNT_EXCEEDS_LIMIT || code === PIN_THROTTLED || isOverrideErrorCode(code)
}

/** Fallback text when the backend sends no message for an override_* code. */
export function overrideErrorFallback(code: string | null | undefined): string {
  switch (code) {
    case 'override_refused':
      return 'That PIN can\'t approve discounts.'
    case PIN_THROTTLED:
      return PIN_THROTTLED_TEXT
    case 'override_limit_exceeded':
      return 'This discount is over that manager\'s own limit.'
    default:
      return 'Manager approval failed.'
  }
}

/** The discount fields of the signed-in clerk (types/Staff). */
export interface StaffDiscountFields {
  canDiscounts?: boolean
  discountLimitPercent?: number
  canOverrideDiscountLimit?: boolean
}

/** The clerk's effective limit in percent. Permissive when the fields are
 *  missing (older backend, or a staff record persisted before this build):
 *  the backend still enforces, the POS just can't pre-check. */
export function effectiveDiscountLimit(staff: StaffDiscountFields | null | undefined): number {
  if (!staff) return UNLIMITED_DISCOUNT_PERCENT
  if (staff.canDiscounts === false) return 0
  const n = staff.discountLimitPercent
  if (typeof n !== 'number' || !Number.isFinite(n)) return UNLIMITED_DISCOUNT_PERCENT
  return Math.min(Math.max(n, 0), UNLIMITED_DISCOUNT_PERCENT)
}

export function hasDiscountLimit(limit: number): boolean {
  return limit < UNLIMITED_DISCOUNT_PERCENT
}

/** Subtitle of the approval dialog. A 0% limit is how the backend expresses
 *  "can't give discounts" (`can_discounts=false`), so say that instead of
 *  "over your 0% limit". */
export function approvalSubtitle(limit: number, requestedPercent?: number | null): string {
  const tail = 'A manager with approval rights enters their PIN.'
  if (limit <= 0) return `You can't give discounts without manager approval. ${tail}`
  const req = requestedPercent != null && Number.isFinite(requestedPercent)
    ? `${formatPercent(requestedPercent)}% is over`
    : 'Over'
  return `${req} your ${formatPercent(limit)}% limit. ${tail}`
}

/** "Your limit: 10%" -- null when there is no limit to show. */
export function discountLimitLabel(limit: number): string | null {
  if (!hasDiscountLimit(limit)) return null
  // 0% is `can_discounts=false`: every discount needs a PIN.
  if (limit <= 0) return 'Discounts need manager approval'
  return `Your limit: ${formatPercent(limit)}%`
}

function formatPercent(n: number): string {
  return String(+n.toFixed(2))
}

/** Float noise only (1e-9): the tolerance proper is the server's half cent
 *  per unit below, never a percentage-point guess. */
const FLOAT_EPS = 1e-9
/** The server tolerates half a cent per unit on a $ amount. */
export const HALF_CENT_PER_UNIT = 0.005

/**
 * Does a discount exceed the limit? Mirrors the server's rule
 * (ops_discount_limit):
 *  - `percent`: `value` is the percent, compared directly with the limit.
 *  - `amount`: `value` is dollars (the TOTAL over `qty` units), judged as
 *    `amount <= limit/100 * base + 0.005 * qty` -- half a cent per unit of
 *    rounding, never a percentage-point tolerance. `base` is the eligible
 *    amount (the line's price x qty, or the receipt's eligible subtotal).
 *    With no usable base the POS can't tell, so it says no and lets the
 *    server decide (a 409 then opens the approval from the cart banner).
 */
export function exceedsLimit(
  kind: 'percent' | 'amount',
  value: number,
  base: number | null | undefined,
  limit: number,
  qty: number = 1,
): boolean {
  if (!hasDiscountLimit(limit)) return false
  if (!Number.isFinite(value) || value <= 0) return false
  if (kind === 'percent') return value > limit + FLOAT_EPS
  if (base == null || !Number.isFinite(base) || base <= 0) return false
  const units = Math.max(1, Math.abs(Number.isFinite(qty) ? qty : 1))
  return value > (limit / 100) * base + HALF_CENT_PER_UNIT * units + FLOAT_EPS
}

/** A manual price below list counts as a discount of (list - price) / list. */
export function priceDropPercent(list: number, price: number): number {
  if (!Number.isFinite(list) || list <= 0 || !Number.isFinite(price)) return 0
  if (price >= list) return 0
  return ((list - price) / list) * 100
}

/**
 * The server's `combined_drop`: a manual price and a line discount on one
 * line are judged as ONE drop from the list price --
 * `(list - price) + unitDiscount` per unit, times qty, against `list x qty`
 * with half a cent per unit (a price at or above list drops nothing). Also
 * the plain cases: no manual price (a $ line discount) or no discount (a
 * manual price alone).
 */
export function combinedDropExceedsLimit(
  args: { list: number; price?: number | null; unitDiscount?: number | null; qty?: number },
  limit: number,
): boolean {
  const { list } = args
  if (!Number.isFinite(list) || list <= 0) return false
  const units = Math.max(1, Math.abs(args.qty ?? 1))
  const price = args.price != null && Number.isFinite(args.price) ? args.price : null
  const priceDrop = price != null && price < list ? list - price : 0
  const disc = args.unitDiscount != null && args.unitDiscount > 0 ? args.unitDiscount : 0
  return exceedsLimit('amount', (priceDrop + disc) * units, list * units, limit, units)
}

/** Limit and requested value off a 409 `discount_exceeds_limit` (`extra`). */
export function limitFromErrorExtra(
  extra: Record<string, unknown> | null | undefined,
): { limit: number | null; requested: number | null } {
  return { limit: num(extra?.limit), requested: num(extra?.requested) }
}

function num(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : parseFloat(String(v))
  return Number.isFinite(n) ? n : null
}

/** Cart banner copy for `discount_exceeds_limit`. */
export function exceedsLimitBannerText(limit: number | null): string {
  if (limit != null && limit <= 0) {
    return 'You can\'t give discounts without manager approval. Get approval or remove it.'
  }
  const lim = limit != null ? `${formatPercent(limit)}% ` : ''
  return `Discount exceeds your ${lim}limit. Get manager approval or remove it.`
}

/** The templates below add their own period; drop ONE trailing period from
 *  a backend message so the result doesn't end in "..". Anything else
 *  (an ellipsis keeps its dots, "?" / "!") is left alone. */
export function stripTrailingPeriod(text: string): string {
  return text.trim().replace(/\.$/, '')
}

/** Throttle copy, everywhere: the backend's lock can last minutes (the
 *  dialog's own 30s lock only stops hammering it). */
export const PIN_THROTTLED_TEXT = 'Too many attempts. Try again in a few minutes.'

/** Banner copy for any discount-gate failure (cart banner and CheckoutModal
 *  share it); null when `code` isn't a gate code. `clerkLimit` fills in the
 *  limit when the 409 carries none. */
export function discountGateBannerText(
  info: { code?: string | null; message?: string | null; extra?: Record<string, unknown> | null } | null | undefined,
  clerkLimit: number,
): string | null {
  const code = info?.code ?? null
  if (code === DISCOUNT_EXCEEDS_LIMIT) {
    const { limit } = limitFromErrorExtra(info?.extra)
    return exceedsLimitBannerText(limit ?? (hasDiscountLimit(clerkLimit) ? clerkLimit : null))
  }
  if (isOverrideErrorCode(code)) {
    return `${stripTrailingPeriod(info?.message || overrideErrorFallback(code))}. Get another manager's approval or remove the discount.`
  }
  if (code === PIN_THROTTLED) return `${PIN_THROTTLED_TEXT} Or remove the discount.`
  return null
}

/** What the approval dialog opens with when re-prompting from a gate error:
 *  the refusal's reason, the fixed throttle copy, or nothing. */
export function gateDialogInitialError(
  info: { code?: string | null; message?: string | null } | null | undefined,
): string | null {
  const code = info?.code ?? null
  if (code === PIN_THROTTLED) return PIN_THROTTLED_TEXT
  if (isOverrideErrorCode(code)) return info?.message || overrideErrorFallback(code)
  return null
}

// ---------------------------------------------------------------------------
// Approvals: tuples and signatures (see the header comment)
// ---------------------------------------------------------------------------

export type ApprovalScope = 'receipt' | 'line' | 'price'

export type ApprovalTuple =
  | {
      scope: 'line'
      productId: number
      lot: string | null
      qty: number
      /** Per-unit discount, cents (what goes on the wire, per unit). */
      unitCents: number
      /** Manual unit price on the same line, cents; null for none. */
      priceCents: number | null
    }
  | { scope: 'price'; productId: number; lot: string | null; qty: number; priceCents: number }
  | {
      scope: 'receipt'
      kind: 'percent' | 'amount'
      /** Percent (4dp) or dollars. */
      value: number
      /** Eligible subtotal the approval preview reported, cents; null until
       *  that preview answered (or when the backend doesn't report it). */
      baseCents: number | null
    }

export interface DiscountApproval {
  /** The approving manager's PIN. Memory only; never persisted or logged. */
  pin: string
  tuple: ApprovalTuple
  /** `signatureOf(tuple)`. */
  signature: string
  /** Receipt scope: `receiptBasisKey` of the cart the base was measured on. */
  basis?: string
  /** When it was recorded (pruneApprovals' grace). */
  addedAt?: number
  /** Seen live on the cart at least once (pruneApprovals). */
  seen?: boolean
}

/** Dollars (or percent) to integer hundredths. */
export function toCents(n: number): number {
  return Math.round(n * 100 + (n >= 0 ? 1e-7 : -1e-7))
}

function lotKey(lot: string | null | undefined): string | null {
  return lot == null || lot === '' ? null : String(lot)
}

function percentKey(v: number): number {
  return +Number(v).toFixed(4)
}

export function signatureOf(t: ApprovalTuple): string {
  switch (t.scope) {
    case 'line':
      return `l|${t.productId}|${t.lot ?? ''}|q${t.qty}|d${t.unitCents}|m${t.priceCents ?? ''}`
    case 'price':
      return `p|${t.productId}|${t.lot ?? ''}|q${t.qty}|m${t.priceCents}`
    case 'receipt':
      return `${receiptValueKeyOf(t)}|b${t.baseCents ?? ''}`
  }
}

/** Kind + value of a receipt tuple, without the base. */
function receiptValueKeyOf(t: { kind: 'percent' | 'amount'; value: number }): string {
  return t.kind === 'percent' ? `r|p${percentKey(t.value)}` : `r|a${toCents(t.value)}`
}

/** Kind + value of the receipt discount the gate checks. Null when there is
 *  nothing it checks: no discount, a coupon, or a SpringBig redemption
 *  (`_system`, never limit-gated). */
export function receiptValueKey(d: CartDiscount | null | undefined): string | null {
  const t = receiptTuple(d, null)
  return t ? receiptValueKeyOf(t) : null
}

export function receiptTuple(
  d: CartDiscount | null | undefined,
  baseCents: number | null,
): Extract<ApprovalTuple, { scope: 'receipt' }> | null {
  if (!d || d.offer_id || d.reward_id) return null
  if (d.percent != null && Number(d.percent) > 0) {
    return { scope: 'receipt', kind: 'percent', value: percentKey(Number(d.percent)), baseCents }
  }
  if (d.amount != null && Number(d.amount) > 0) {
    return { scope: 'receipt', kind: 'amount', value: toCents(Number(d.amount)) / 100, baseCents }
  }
  return null
}

/** Signature of the receipt discount at a base. */
export function receiptApprovalSignature(
  d: CartDiscount | null | undefined,
  baseCents: number | null = null,
): string | null {
  const t = receiptTuple(d, baseCents)
  return t ? signatureOf(t) : null
}

/** What the receipt base is measured on: customer, tax exemption and every
 *  line's product/lot/qty/price, gift-card balance included (a balance
 *  change moves the base). Line discounts and metadata are off the wire
 *  under a receipt discount, so they don't count. */
export function receiptBasisKey(
  cart: Pick<Cart, 'lines'> & Partial<Pick<Cart, 'customer_id' | 'exempt_taxes'>>,
): string {
  const lines = cart.lines
    .map((l) => `${l.product_id}|${lotKey(l.lot_number) ?? ''}|${l.qty}|${l.manual_price ?? ''}|${l.gift_card_number ?? ''}|${l.gift_card_balance ?? ''}`)
    .sort()
    .join(';')
  return `${cart.customer_id ?? ''}|${cart.exempt_taxes ? 1 : 0}|${lines}`
}

type TupleLine = Pick<CartLine, 'product_id' | 'qty' | 'lot_number' | 'discount_amount' | 'manual_price'>
  & Partial<Pick<CartLine, 'gift_card_number'>>

function isGiftCardLine(line: Partial<Pick<CartLine, 'gift_card_number'>>): boolean {
  return line.gift_card_number != null && line.gift_card_number !== ''
}

/** The bound tuple of one line: `line` when it carries a discount (with its
 *  manual price, judged together), `price` for a manual price alone, null
 *  otherwise -- and always null for a gift card (its manual price is the
 *  card's balance, not a discount). Per unit, because that is what goes on
 *  the wire (`cartToRequest` divides the stored line total by qty). */
export function lineTuple(line: TupleLine): Extract<ApprovalTuple, { scope: 'line' | 'price' }> | null {
  if (isGiftCardLine(line)) return null
  const qty = line.qty
  const units = Math.max(1, Math.abs(qty || 1))
  const lot = lotKey(line.lot_number)
  const price = line.manual_price != null && line.manual_price !== ''
    ? Number(line.manual_price)
    : null
  const priceCents = price != null && Number.isFinite(price) ? toCents(price) : null
  if (line.discount_amount != null && line.discount_amount > 0) {
    return {
      scope: 'line',
      productId: line.product_id,
      lot,
      qty,
      unitCents: toCents(line.discount_amount / units),
      priceCents,
    }
  }
  if (priceCents != null) return { scope: 'price', productId: line.product_id, lot, qty, priceCents }
  return null
}

/** Signature of one line's discount/price; null when nothing on it is gated. */
export function lineApprovalSignature(line: TupleLine): string | null {
  const t = lineTuple(line)
  return t ? signatureOf(t) : null
}

type ApprovalCart = Pick<Cart, 'lines' | 'discount'> & Partial<Pick<Cart, 'customer_id' | 'exempt_taxes'>>

/** Is `a` still the value on `cart`? Receipt: same kind + value on the
 *  cart discount and the same basis. Line/price: some line has exactly
 *  that tuple (a line approval under a receipt discount still matches --
 *  the value is on the cart, just off the wire). */
export function approvalMatchesCart(
  a: Pick<DiscountApproval, 'tuple' | 'signature' | 'basis'>,
  cart: ApprovalCart,
): boolean {
  if (a.tuple.scope === 'receipt') {
    const key = receiptValueKey(cart.discount)
    return key != null && key === receiptValueKeyOf(a.tuple) && a.basis === receiptBasisKey(cart)
  }
  return cart.lines.some((l) => lineApprovalSignature(l) === a.signature)
}

/** Minimal request shape `attachApprovals` works on (api/cart's
 *  CartPreviewRequest satisfies it; kept structural to avoid an import
 *  cycle with the api layer). */
export interface ApprovalRequestShape {
  items: Array<{ override_pin?: string }>
  discount?: (CartDiscount & { override_pin?: string }) | null
}

/**
 * Put approving PINs on a request built from `cart`. `req.items` must be in
 * `cart.lines` order (cartToRequest maps 1:1). Line-discount PINs are skipped
 * while a receipt discount is active (the line discounts are off the wire
 * then); a manual price stays on the wire, so its PIN still rides. Returns a
 * new object; `req` is not mutated.
 */
export function attachApprovals<T extends ApprovalRequestShape>(
  req: T,
  cart: ApprovalCart,
  approvals: readonly DiscountApproval[] | null | undefined,
): T {
  if (!approvals || approvals.length === 0) return req
  let discount = req.discount
  if (discount) {
    const r = approvals.find((a) => a.tuple.scope === 'receipt' && approvalMatchesCart(a, cart))
    if (r) discount = { ...discount, override_pin: r.pin }
  }
  const cartLevel = !!cart.discount
  const items = req.items.map((item, i) => {
    const line = cart.lines[i]
    if (!line) return item
    const t = lineTuple(line)
    if (!t || (cartLevel && t.scope === 'line')) return item
    const sig = signatureOf(t)
    const a = approvals.find((x) => x.tuple.scope === t.scope && x.signature === sig)
    return a ? { ...item, override_pin: a.pin } : item
  })
  return { ...req, items, ...(discount !== undefined ? { discount } : {}) }
}

/** How long a just-recorded approval survives prunes before it was ever
 *  seen on the cart: it is recorded from the approval preview BEFORE the
 *  caller applies the value, and a multi-step posStore write can mirror an
 *  intermediate shape in between. */
export const APPROVAL_PRUNE_GRACE_MS = 3_000

/**
 * Drop approvals whose tuple is no longer on the cart (value, qty, lot or
 * receipt base changed, or the discount was removed). Metadata-only changes
 * (reason, scan_confirmed) never prune: they are not in the tuple.
 * `eligibleBaseCents`: the eligible base of a preview priced for exactly this
 * cart; a receipt approval recorded at another base is dropped (the server
 * would refuse to reuse it). An approval not yet seen on the cart is kept for
 * APPROVAL_PRUNE_GRACE_MS. Returns the same array when nothing changed, so a
 * store write can be skipped.
 */
export function pruneApprovals(
  approvals: readonly DiscountApproval[],
  cart: ApprovalCart,
  now: number,
  opts: { eligibleBaseCents?: number | null } = {},
): readonly DiscountApproval[] {
  if (approvals.length === 0) return approvals
  let changed = false
  const kept: DiscountApproval[] = []
  for (const a of approvals) {
    let live = approvalMatchesCart(a, cart)
    if (live && a.tuple.scope === 'receipt' && opts.eligibleBaseCents != null && a.tuple.baseCents != null) {
      live = Math.abs(a.tuple.baseCents - opts.eligibleBaseCents) <= 1
    }
    if (live) {
      if (a.seen) kept.push(a)
      else {
        kept.push({ ...a, seen: true })
        changed = true
      }
    } else if (!a.seen && now - (a.addedAt ?? 0) < APPROVAL_PRUNE_GRACE_MS) {
      kept.push(a)
    } else {
      changed = true
    }
  }
  return changed ? kept : approvals
}

/** The tuple a 409 `discount_exceeds_limit` refused (`extra`: scope,
 *  product_id, lot, qty, requested); null when the backend didn't say. */
export interface RefusedTuple {
  scope: ApprovalScope
  productId: number | null
  /** undefined: not sent (any lot). */
  lot: string | null | undefined
  qty: number | null
  requested: number | null
}

export function refusedFromExtra(extra: Record<string, unknown> | null | undefined): RefusedTuple | null {
  const scope = extra?.scope
  if (scope !== 'receipt' && scope !== 'line' && scope !== 'price') return null
  return {
    scope,
    productId: num(extra?.product_id),
    lot: extra && 'lot' in extra ? lotKey(extra.lot as string | null) : undefined,
    qty: num(extra?.qty),
    requested: num(extra?.requested),
  }
}

/** What a 409 `discount_exceeds_limit` during an approval refused, named
 *  from the cart it was priced for: "Blue Dream (Qty 2 · Lot A · $3.00 off
 *  each) wasn't covered..." / "The receipt discount (15% off) ...". */
export function refusalText(
  extra: Record<string, unknown> | null | undefined,
  cart: ApprovalCart & Partial<Pick<Cart, 'lines'>>,
): string {
  const tail = 'Close this and apply it again.'
  const r = refusedFromExtra(extra)
  const requested = r?.requested != null ? ` (${formatPercent(r.requested)}% requested)` : ''
  if (r?.scope === 'receipt') {
    const item = receiptApprovalItem(cart, null)
    return `The receipt discount${item ? ` (${item.detail})` : ''} is over the limit${requested} and this approval didn't cover it. ${tail}`
  }
  if (r) {
    const line = cart.lines.find((l) => {
      const t = lineTuple(l)
      return t != null && tupleMatchesRefusal(t, r)
    })
    const item = line ? lineApprovalItem(line) : null
    if (item) return `${item.title} (${item.detail}) is over the limit${requested} and this approval didn't cover it. ${tail}`
    if (r.productId != null) return `Item #${r.productId} is over the limit${requested} and this approval didn't cover it. ${tail}`
  }
  return `The discount changed while approving. ${tail}`
}

/** Does an approval/line tuple fall under the refusal? Receipt: any receipt
 *  tuple. Line/price (the server may record a price+discount line under
 *  either): same product, and the lot / qty when the 409 names them. */
export function tupleMatchesRefusal(t: ApprovalTuple, r: RefusedTuple): boolean {
  if (r.scope === 'receipt') return t.scope === 'receipt'
  if (t.scope === 'receipt') return false
  if (r.productId == null || t.productId !== r.productId) return false
  // A line added without a lot is priced on a lot the SERVER chose, and the
  // 409 names that lot: a POS tuple with no lot matches any lot.
  if (r.lot !== undefined && t.lot !== null && t.lot !== r.lot) return false
  if (r.qty != null && t.qty !== r.qty) return false
  return true
}

/** A 409 `discount_exceeds_limit` while approvals were riding: drop the
 *  ones for the refused tuple (the server wouldn't take them), keep the
 *  rest. Same array when nothing matched. */
export function dropRefusedApprovals(
  approvals: readonly DiscountApproval[],
  extra: Record<string, unknown> | null | undefined,
): readonly DiscountApproval[] {
  const r = refusedFromExtra(extra)
  if (!r || approvals.length === 0) return approvals
  const kept = approvals.filter((a) => !tupleMatchesRefusal(a.tuple, r))
  return kept.length === approvals.length ? approvals : kept
}

/** Drop the approvals with these signatures (an approval recorded for a
 *  dialog that was closed before it resolved must not linger, or
 *  `isItemApproved` would silently reuse it). Same array when none match. */
export function removeApprovalsBySignature(
  approvals: readonly DiscountApproval[],
  signatures: readonly string[],
): readonly DiscountApproval[] {
  if (signatures.length === 0) return approvals
  const drop = new Set(signatures)
  const kept = approvals.filter((a) => !drop.has(a.signature))
  return kept.length === approvals.length ? approvals : kept
}

/** Add (or replace, same tuple) an approval. A receipt approval replaces any
 *  other receipt approval: there is one receipt discount. */
export function addApproval(
  approvals: readonly DiscountApproval[],
  next: DiscountApproval,
): DiscountApproval[] {
  return [
    ...approvals.filter((a) => (next.tuple.scope === 'receipt'
      ? a.tuple.scope !== 'receipt'
      : a.signature !== next.signature)),
    next,
  ]
}

// ---------------------------------------------------------------------------
// What the approval dialog lists (one row per value the PIN will approve)
// ---------------------------------------------------------------------------

/** One value the manager is asked to approve. The dialog lists every one,
 *  and the PIN is attached to exactly these tuples. */
export interface ApprovalItem {
  tuple: ApprovalTuple
  signature: string
  /** Receipt scope: `receiptBasisKey` of the cart it applies to. */
  basis?: string
  title: string
  detail: string
}

function money(n: number): string {
  return `$${n.toFixed(2)}`
}

/** Dialog row for one line's gated value; null when nothing on it is gated. */
export function lineApprovalItem(
  line: TupleLine & Partial<Pick<CartLine, 'display'>>,
): ApprovalItem | null {
  const t = lineTuple(line)
  if (!t) return null
  const parts = [`Qty ${t.qty}`]
  if (t.lot) parts.push(`Lot ${t.lot}`)
  if (t.scope === 'line') {
    const each = t.unitCents / 100
    const units = Math.abs(t.qty)
    parts.push(units > 1 ? `${money(each)} off each (${money(each * units)})` : `${money(each)} off`)
  }
  if (t.priceCents != null) parts.push(`price ${money(t.priceCents / 100)}`)
  const title = (line.display?.description ?? '').trim() || `Item #${line.product_id}`
  return { tuple: t, signature: signatureOf(t), title, detail: parts.join(' · ') }
}

/** Dialog row for the receipt discount. `base`: the eligible subtotal it is
 *  measured on (shown; the recorded base comes from the approval preview). */
export function receiptApprovalItem(
  cart: ApprovalCart,
  base: number | null,
): ApprovalItem | null {
  const baseCents = base != null && Number.isFinite(base) && base > 0 ? toCents(base) : null
  const t = receiptTuple(cart.discount, baseCents)
  if (!t) return null
  const value = t.kind === 'percent' ? `${formatPercent(t.value)}% off` : `${money(t.value)} off`
  return {
    tuple: t,
    signature: signatureOf(t),
    basis: receiptBasisKey(cart),
    title: 'Receipt discount',
    detail: baseCents != null ? `${value} a ${money(baseCents / 100)} subtotal` : value,
  }
}

/** Every gated value on `cart`: the receipt discount (plus any manual price,
 *  which stays on the wire under it), or each discounted / manually priced
 *  line. Gift cards never. */
export function gatedApprovalItems(
  cart: ApprovalCart & Partial<Pick<Cart, 'lines'>>,
  base: number | null,
): ApprovalItem[] {
  const out: ApprovalItem[] = []
  if (cart.discount) {
    const r = receiptApprovalItem(cart, base)
    if (r) out.push(r)
  }
  for (const l of cart.lines) {
    const item = lineApprovalItem(l)
    if (!item || (cart.discount && item.tuple.scope === 'line')) continue
    if (!out.some((o) => o.signature === item.signature)) out.push(item)
  }
  return out
}

/** Is this value already approved (an approval for its exact tuple)? */
export function isItemApproved(
  item: Pick<ApprovalItem, 'tuple' | 'signature' | 'basis'>,
  approvals: readonly DiscountApproval[],
): boolean {
  if (item.tuple.scope === 'receipt') {
    const key = receiptValueKeyOf(item.tuple)
    return approvals.some((a) => a.tuple.scope === 'receipt'
      && receiptValueKeyOf(a.tuple) === key && a.basis === item.basis)
  }
  return approvals.some((a) => a.signature === item.signature)
}

/**
 * The values the cart banner's Approve asks for after a gate error: the
 * tuple the 409 names (`extra` scope/product_id/lot/qty) when it names one
 * that is on the cart; otherwise every gated value not already approved
 * (or, if all are, every gated value), each listed in the dialog.
 */
export function approvalItemsForRefusal(
  cart: ApprovalCart,
  extra: Record<string, unknown> | null | undefined,
  opts: { base: number | null; approvals: readonly DiscountApproval[] },
): ApprovalItem[] {
  const all = gatedApprovalItems(cart, opts.base)
  const refused = refusedFromExtra(extra)
  if (refused) {
    const named = all.filter((i) => tupleMatchesRefusal(i.tuple, refused))
    if (named.length) return named
  }
  const open = all.filter((i) => !isItemApproved(i, opts.approvals))
  return open.length ? open : all
}

/** Pending approvals for `items` under one PIN. */
export function approvalsFor(items: readonly ApprovalItem[], pin: string): DiscountApproval[] {
  return items.map((i) => ({
    pin,
    tuple: i.tuple,
    signature: i.signature,
    ...(i.basis !== undefined ? { basis: i.basis } : {}),
  }))
}

/** A receipt approval re-bound to the base the approval preview reported. */
export function withReceiptBase<A extends Pick<DiscountApproval, 'tuple' | 'signature'>>(
  a: A,
  baseCents: number | null,
): A {
  if (a.tuple.scope !== 'receipt' || baseCents == null) return a
  const tuple = { ...a.tuple, baseCents }
  return { ...a, tuple, signature: signatureOf(tuple) }
}

/**
 * The eligible base of a receipt $ discount, from a preview's totals: the
 * backend's `discount_eligible_subtotal` when it sends one; otherwise the
 * subtotal before the receipt discount (`subtotal`, plus `discount_amount`
 * when a manual receipt discount was already applied to that preview).
 */
export function eligibleBaseOf(
  totals: PreviewTotals | null | undefined,
  receiptDiscountApplied: boolean,
): number | null {
  if (!totals) return null
  const explicit = num(totals.discount_eligible_subtotal)
  if (explicit != null) return explicit > 0 ? explicit : null
  const sub = num(totals.subtotal)
  if (sub == null) return null
  const disc = receiptDiscountApplied ? num(totals.discount_amount) ?? 0 : 0
  const base = sub + Math.max(disc, 0)
  return base > 0 ? +base.toFixed(2) : null
}

// ---------------------------------------------------------------------------
// Banner "Remove"
// ---------------------------------------------------------------------------

/** Minimal line shape for `linesToRemove` (cartStore CartLine). */
export type LimitCheckLine = TupleLine & Pick<CartLine, 'priced' | 'display'>

/**
 * The line discounts the banner's "Remove" clears after a 409
 * `discount_exceeds_limit`: the refused tuple's lines when the 409 names
 * them; otherwise the unapproved lines whose discount measures over `limit`
 * against the SERVER-priced line (`priced.price` x qty; the catalog estimate
 * only before any preview). Never an in-limit discount: when nothing
 * measures over, nothing is returned (the caller says so).
 */
export function linesToRemove<L extends LimitCheckLine>(
  lines: readonly L[],
  limit: number,
  opts: {
    extra?: Record<string, unknown> | null
    isApproved?: (line: L) => boolean
  } = {},
): L[] {
  const discounted = lines.filter((l) => (l.discount_amount ?? 0) > 0 && lineTuple(l) != null)
  const refused = refusedFromExtra(opts.extra)
  if (refused && refused.scope !== 'receipt') {
    const named = discounted.filter((l) => tupleMatchesRefusal(lineTuple(l)!, refused))
    if (named.length) return named
  }
  const isApproved = opts.isApproved ?? (() => false)
  return discounted.filter((l) => {
    if (isApproved(l)) return false
    const units = Math.max(1, Math.abs(l.qty || 1))
    const serverUnit = l.priced ? parseFloat(l.priced.price) : NaN
    const unit = Number.isFinite(serverUnit) && serverUnit > 0 ? serverUnit : l.display.estimated_price
    return exceedsLimit('amount', l.discount_amount ?? 0, unit * units, limit, units)
  })
}

/** Row amounts round to cents (percent rows to hundredths): a recorded row
 *  matches within this many hundredths, integer-compared. */
export const ROW_MATCH_TOLERANCE_CENTS = 2

function nearHundredths(a: number | null, b: number | null | undefined): boolean {
  return a != null && b != null && Number.isFinite(b)
    && Math.abs(toCents(a) - toCents(b)) <= ROW_MATCH_TOLERANCE_CENTS
}

/** The priced rows of the approval preview, for rows that name their line
 *  only by `item_id`. */
export interface PreviewItemRef {
  id: number
  product_id: number
  batch_number?: string | null
  lot_number?: string | null
}

/** A row carrying the bound tuple must carry THIS line's. Its product is
 *  `product_id`, else the preview item its `item_id` points at; an older
 *  backend's row with neither is judged on scope + value alone. A POS line
 *  added without a lot is priced on a lot the server chose, which the row
 *  carries: a tuple with no lot accepts any row lot. */
function rowMatchesLine(
  r: DiscountOverrideRow,
  t: { productId: number; lot: string | null; qty: number },
  items: readonly PreviewItemRef[] | null | undefined,
  cartLines: readonly Pick<CartLine, 'product_id' | 'lot_number'>[],
): boolean {
  const viaItem = r.item_id != null ? items?.find((i) => i.id === r.item_id) ?? null : null
  const productId = r.product_id != null ? Number(r.product_id) : viaItem?.product_id ?? null
  if (productId != null && productId !== t.productId) return false
  const itemLot = viaItem ? lotKey(viaItem.batch_number ?? viaItem.lot_number ?? null) : undefined
  const ownLot = r.lot !== undefined ? lotKey(r.lot) : undefined
  if (t.lot !== null) {
    const rowLot = ownLot !== undefined ? ownLot : itemLot
    if (rowLot !== undefined && rowLot !== t.lot) return false
  } else {
    // No lot on the POS line: the server chose one. Prefer the priced line
    // the row points at (item_id), then the row's own lot -- and never a lot
    // another cart line of this product is pinned to (that row is ITS row).
    const rowLot = itemLot !== undefined ? itemLot : ownLot
    if (rowLot != null && cartLines.some((l) => l.product_id === t.productId
      && lotKey(l.lot_number) === rowLot)) return false
  }
  const q = num(r.qty)
  if (q != null && q !== t.qty) return false
  return true
}

/**
 * The requested approvals that the approval preview actually recorded: a
 * live `discount_overrides` row of the same scope for the same tuple.
 *  - receipt: the cart discount is still this kind + value on this basis,
 *    and a `receipt` row has that percent (or amount) -- and, when the row
 *    reports it, the same base.
 *  - line: a `line` row for that line whose amount is the line's discount
 *    total (or, for a line that also has a manual price, a `price` row for
 *    it: the server judges the two as one drop).
 *  - price: a `price` row for that line.
 * Anything else (no row: within the limit server-side, or it never rode the
 * request) is not an approval and must not be recorded.
 */
export function confirmedApprovals<A extends Pick<DiscountApproval, 'tuple' | 'signature' | 'basis'>>(
  requested: readonly A[],
  cart: ApprovalCart,
  rows: readonly DiscountOverrideRow[] | null | undefined,
  items?: readonly PreviewItemRef[] | null,
): A[] {
  const live = (rows ?? []).filter((r) => !r.cancelled_at)
  if (live.length === 0) return []
  return requested.filter((a) => {
    const t = a.tuple
    if (t.scope === 'receipt') {
      if (!approvalMatchesCart(a, cart)) return false
      return live.some((r) => {
        if (r.scope !== 'receipt') return false
        const valueOk = t.kind === 'percent'
          ? nearHundredths(num(r.percent), t.value)
          : nearHundredths(num(r.amount), t.value)
        if (!valueOk) return false
        const rowBase = num(r.base_amount)
        return rowBase == null || t.baseCents == null || Math.abs(toCents(rowBase) - t.baseCents) <= 1
      })
    }
    // A receipt discount takes the line discounts off the wire.
    if (t.scope === 'line' && cart.discount) return false
    const lines = cart.lines.filter((l) => lineApprovalSignature(l) === a.signature)
    return lines.some((l) => live.some((r) => {
      if (!rowMatchesLine(r, t, items, cart.lines)) return false
      if (t.scope === 'price') return r.scope === 'price'
      if (r.scope === 'line') return nearHundredths(num(r.amount), l.discount_amount ?? null)
      return r.scope === 'price' && t.priceCents != null
    }))
  })
}

/** What an approval preview lets the POS record: the requested approvals,
 *  receipt ones re-bound to the base that preview reports, that a live row
 *  confirms (see confirmedApprovals). */
export function approvalsToRecord<A extends Pick<DiscountApproval, 'tuple' | 'signature' | 'basis'>>(
  pending: readonly A[],
  cart: ApprovalCart,
  preview: { totals?: PreviewTotals | null; discount_overrides?: readonly DiscountOverrideRow[] | null; items?: readonly PreviewItemRef[] | null },
): A[] {
  const base = eligibleBaseOf(preview.totals, true)
  const priced = pending.map((a) => withReceiptBase(a, base != null ? toCents(base) : null))
  return confirmedApprovals(priced, cart, preview.discount_overrides, preview.items)
}

// ---------------------------------------------------------------------------
// cartStore bookkeeping (pure halves of primePreview / receiptRebound)
// ---------------------------------------------------------------------------

/** An approval/probe preview held for the cart about to be applied. */
export interface PrimedPreview<P> {
  fingerprint: string
  preview: P
  at: number
}

/** How long a primed preview stays reusable. The apply that follows an
 *  approval is synchronous, so this only has to cover the bridge's mirror
 *  and one render; short so time-based pricing can't go stale. */
export const PRIMED_PREVIEW_TTL_MS = 5_000

/** The primed preview when it was priced for exactly `fingerprint` and is
 *  still fresh, else null. The caller empties the slot either way: single
 *  use, so a mismatched shape can never pick it up later. */
export function consumePrimedPreview<P>(
  primed: PrimedPreview<P> | null,
  fingerprint: string,
  now: number,
  ttlMs: number = PRIMED_PREVIEW_TTL_MS,
): P | null {
  if (!primed || primed.fingerprint !== fingerprint) return null
  if (now - primed.at > ttlMs) return null
  return primed.preview
}

/** posStore.currentReceiptId moved to `receiptId`: keep the approvals only
 *  when that is the receipt this cart itself just committed (a partially
 *  paid receipt keeps re-previewing with them). Any other move -- a recall
 *  or unhold, a new sale, a detach to null -- drops them. */
export function keepApprovalsOnRebind(
  receiptId: number | null,
  committedReceiptId: number | null,
): boolean {
  return receiptId != null && receiptId === committedReceiptId
}

// ---------------------------------------------------------------------------
// Test carts for a validation preview
// ---------------------------------------------------------------------------

/** A line discount the cashier is about to apply, in the cart's storage
 *  convention (LINE-TOTAL dollars). */
export interface LineDiscountTarget {
  product_id: number
  lot_number: string | null
  qty: number
  discount_amount: number
  /** Reason metadata, so the test cart fingerprints like the applied one
   *  (cartFingerprint includes it) and the primed preview is reused. */
  discount_reason_id?: number | null
  discount_reason_other?: string | null
  /** Name for a line that isn't on the cart yet (the dialog lists it). */
  description?: string
}

/** The cart as it would be once `targets` land: matching lines get the new
 *  qty/discount, missing ones are appended. Used to price an approval on
 *  /cart/preview BEFORE committing the change to the stores. Pure. */
export function cartWithLineDiscounts<C extends Pick<Cart, 'lines'>>(
  cart: C,
  targets: readonly LineDiscountTarget[],
): C {
  const lines = cart.lines.slice()
  for (const t of targets) {
    const i = lines.findIndex(
      (l) => l.product_id === t.product_id && (l.lot_number ?? null) === (t.lot_number ?? null),
    )
    const discount_amount = t.discount_amount > 0 ? t.discount_amount : null
    // Reasons ride only with a live discount (same rule as the bridge).
    const reasons = {
      discount_reason_id: discount_amount != null ? t.discount_reason_id ?? null : null,
      discount_reason_other: discount_amount != null ? t.discount_reason_other ?? null : null,
    }
    if (i >= 0) {
      lines[i] = { ...lines[i], qty: t.qty, discount_amount, ...reasons }
    } else {
      lines.push({
        localId: `approval_${t.product_id}_${t.lot_number ?? ''}`,
        product_id: t.product_id,
        qty: t.qty,
        lot_number: t.lot_number ?? null,
        manual_price: null,
        priced: null,
        state: 'pending',
        display: { description: t.description ?? '', image: null, estimated_price: 0 },
        discount_amount,
        ...reasons,
      })
    }
  }
  return { ...cart, lines }
}

// ---------------------------------------------------------------------------
// "Approved by" display
// ---------------------------------------------------------------------------

/** Distinct approver names for the live (not cancelled) overrides of a scope.
 *  `scope: 'line'` also covers manual-price (`price`) approvals. */
export function approvedByNames(
  rows: readonly DiscountOverrideRow[] | null | undefined,
  scope: 'receipt' | 'line' | 'any' = 'any',
): string[] {
  if (!rows) return []
  const names: string[] = []
  for (const r of rows) {
    if (r.cancelled_at) continue
    if (scope === 'receipt' && r.scope !== 'receipt') continue
    if (scope === 'line' && r.scope === 'receipt') continue
    const n = (r.override_by_name ?? '').trim()
    if (n && !names.includes(n)) names.push(n)
  }
  return names
}

/** "approved by J. Smith" / "approved by J. Smith, A. Lee"; null for none. */
export function approvedByText(names: readonly string[]): string | null {
  return names.length ? `approved by ${names.join(', ')}` : null
}
